import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { config } from "config";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "shared";
import { USERID_CLAIMS } from "system-constants";

type ChartWebsocketProps = {
  encounterId: string;
  chartCode: string;
  sectionCode: string;
  onReload?: () => void;
  sectionName: string;
};

type WebsocketConnection = {
  UserId: string;
  UserName: string;
  EncounterId: string;
  ChartCode: string;
  SectionCode: string;
};

type WebsocketResponse = { MoreUsersConnected: boolean };

function ChartWebsocket({
  encounterId,
  chartCode,
  sectionCode,
  onReload,
  sectionName,
}: ChartWebsocketProps) {
  const { user, getAccessTokenSilently } = useAuth0();

  const socketRef = useRef<WebSocket | null>(null);
  const retryCount = useRef<number>(0);

  const [isConnected, setIsConnected] = useState(false);
  const [moreUsersConnected, setMoreUsersConnected] = useState(false);

  const MAX_RETRIES = 5;
  const RETRY_DELAY = 5;

  const handleReconnect = () => {
    if (retryCount.current >= MAX_RETRIES) {
      return;
    }

    let counter = RETRY_DELAY;
    retryCount.current += 1;

    const interval = setInterval(() => {
      counter -= 1;

      if (counter <= 0) {
        clearInterval(interval);
        connectWebSocket();
      }
    }, 1000);
  };

  const connectWebSocket = useCallback(async () => {
    const token = await getAccessTokenSilently({ ignoreCache: true });
    const userId = user ? user[USERID_CLAIMS] : "";

    const identityApiBaseUrl = config.identityApiBaseUrl
      ?.replace("https", "wss")
      .replace("http", "wss");

    socketRef.current = new WebSocket(
      `${identityApiBaseUrl}${
        identityApiBaseUrl?.endsWith("/") ? "" : "/"
      }ws?access_token=${token}`
    );

    socketRef.current.onopen = () => {
      // Wait a tick to ensure the socket is fully ready
      setTimeout(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          const connection: WebsocketConnection = {
            UserId: userId,
            UserName: user?.name || user?.email || userId,
            EncounterId: encounterId,
            ChartCode: chartCode,
            SectionCode: sectionCode,
          };

          socketRef.current.send(JSON.stringify(connection));
          setIsConnected(true);
          retryCount.current = 0;
        }
      }, 0);
    };

    socketRef.current.onmessage = (event) => {
      const response: WebsocketResponse = JSON.parse(event.data);
      setMoreUsersConnected(response.MoreUsersConnected);
    };

    socketRef.current.onclose = () => {
      setIsConnected(false);
      setMoreUsersConnected(false);
      handleReconnect();
    };

    socketRef.current.onerror = () => {
      socketRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently, user]);

  useEffect(() => {
    connectWebSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    onReload?.();
    setMoreUsersConnected(false);
  };

  return (
    <Modal
      isOpen={isConnected && moreUsersConnected}
      onClose={() => {}}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text fontWeight="600" fontSize="1.125rem" textAlign="center">
            Warning!
          </Text>
        </ModalHeader>
        <ModalBody>
          <Box display="flex" flexDir="column" gap={2}>
            <Text textAlign="center">
              {`Another user has updated and is possibly updating this patient's ${sectionName}!`}
            </Text>
            <Text textAlign="center">
              Press the refresh button to see the most recent changes.
            </Text>
          </Box>
        </ModalBody>

        <ModalFooter
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={4}
        >
          <Button
            flex={1}
            onClick={() => setMoreUsersConnected(false)}
            colorScheme="gray"
          >
            Cancel
          </Button>

          <Button flex={1} onClick={handleRefresh}>
            Refresh
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export { ChartWebsocket };
